import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onlineUsers: Set<string>;
  emit: (event: string, data: any) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  onlineUsers: new Set(),
  emit: () => {}
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
  children: React.ReactNode;
  session?: any;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children, session }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Conectar ao servidor Socket.IO
    // Em produção (porta 80/443 via Nginx), conecta sem porta
    // Em dev (Vite 5173), conecta na porta 5001
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    const hostname = window.location.hostname;
    const isDev = window.location.port === '5173' || window.location.port === '5174';
    const socketUrl = isDev ? `${protocol}//${hostname}:5001` : `${protocol}//${hostname}`;
    
    console.log('🔌 [SOCKET] Conectando ao backend:', socketUrl);
    
    const socketInstance = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socketInstance.on('connect', () => {
      console.log('🔌 [SOCKET] Conectado ao servidor');
      setIsConnected(true);
      
      // Se tem sessão, fazer login automático E ENTRAR NA SALA DA EMPRESA
      if (session?.usuario && session?.empresa) {
        console.log('🏢 [SOCKET] Entrando na sala da empresa:', session.empresa.id);
        
        socketInstance.emit('user_login', {
          user_id: session.usuario.id,
          nome: session.usuario.nome,
          email: session.usuario.email,
          empresa_id: session.empresa.id
        });
        
        // 🔥 ENTRAR NA SALA DA EMPRESA para receber eventos
        socketInstance.emit('join_room', {
          room: `empresa_${session.empresa.id}`,
          user_id: session.usuario.id,
          nome: session.usuario.nome
        });
      }
    });

    socketInstance.on('disconnect', () => {
      console.log('🔴 [SOCKET] Desconectado do servidor');
      setIsConnected(false);
    });

    socketInstance.on('connection_response', (data) => {
      console.log('✅ [SOCKET] Resposta de conexão:', data);
    });

    socketInstance.on('user_online', (data) => {
      console.log('🟢 [SOCKET] Usuário online:', data.nome);
      setOnlineUsers(prev => new Set([...prev, data.user_id]));
    });

    socketInstance.on('user_offline', (data) => {
      console.log('🔴 [SOCKET] Usuário offline:', data.nome);
      setOnlineUsers(prev => {
        const next = new Set(prev);
        next.delete(data.user_id);
        return next;
      });
    });

    socketInstance.on('online_users_list', (data) => {
      console.log('📋 [SOCKET] Lista de usuários online:', data.users);
      const userIds = data.users.map((u: any) => u.user_id);
      setOnlineUsers(new Set(userIds));
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, [session]);

  // Heartbeat a cada 15 segundos
  useEffect(() => {
    if (!socket || !isConnected || !session?.usuario?.id) return;

    const interval = setInterval(() => {
      socket.emit('heartbeat', {
        user_id: session.usuario.id
      });
    }, 15000);

    return () => clearInterval(interval);
  }, [socket, isConnected, session]);

  const emit = useCallback((event: string, data: any) => {
    if (socket && isConnected) {
      socket.emit(event, data);
    }
  }, [socket, isConnected]);

  return (
    <SocketContext.Provider value={{ socket, isConnected, onlineUsers, emit }}>
      {children}
    </SocketContext.Provider>
  );
};
